import { ui } from '../assignment/assignment-ui';

/**
 * Tailwind class strings shared by the Briefing tab's sections and drawers, on top of the
 * Manage Brief `ui` map. Amber is the handover's "gaps" palette; it has no dark-mode token, so
 * it is paired with dark variants here.
 */

const chip = `inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-colors duration-[140ms] ${ui.focusRing}`;
const segBtn = `min-w-11 cursor-pointer rounded-md px-3 py-1.5 text-[13px] font-bold transition-colors duration-[140ms] ${ui.focusRing}`;

export const bf = {
  ...ui,

  /** Section card states. */
  section: `${ui.card} scroll-mt-4 transition-shadow duration-[1200ms]`,
  sectionMissing: 'rounded-xl border-[1.5px] border-dashed border-[#e8a54b] bg-(--mba-surface) scroll-mt-4',
  sectionWeak: 'rounded-xl border-[1.5px] border-solid border-[#e8a54b] bg-(--mba-surface) scroll-mt-4',
  iconTile: 'grid size-10 shrink-0 place-items-center rounded-[10px] bg-(--app-accent-soft) text-(--app-accent-strong)',
  iconTileAmber: 'grid size-10 shrink-0 place-items-center rounded-[10px] bg-[#fef3e2] text-[#b45309] dark:bg-amber-500/15 dark:text-amber-300',
  h2: 'm-0 font-(family-name:--font-display) text-[26px] leading-[0.9] font-bold tracking-[-0.02em] text-(--fg-1)',
  helper: 'max-w-[62ch] text-[13px] leading-[1.45] text-(--fg-3)',

  pillSet: 'inline-flex items-center gap-1 rounded-full bg-[rgba(11,143,78,0.1)] px-[9px] py-[3px] font-(family-name:--font-fallback) text-[11px] font-bold text-(--success)',
  pillAmber: 'inline-flex items-center gap-1 rounded-full bg-[#fef3e2] px-[9px] py-[3px] font-(family-name:--font-fallback) text-[11px] font-bold text-[#b45309] dark:bg-amber-500/15 dark:text-amber-300',

  /** "No rules set…" block with a Set up button. */
  empty: 'flex flex-wrap items-center gap-3 rounded-lg bg-[#fffaf2] px-4 py-3.5 dark:bg-amber-500/10',
  emptyTitle: 'font-(family-name:--font-display) text-lg leading-tight font-bold text-(--fg-1)',

  /** Grey info row; add `lock` styling with `infoLock`. */
  info: 'flex items-start gap-2.5 rounded-lg bg-(--bg-alt) px-3.5 py-2.5 text-[13px] leading-[1.45] text-(--fg-2)',
  infoLock: 'flex items-start gap-2.5 rounded-lg bg-(--app-accent-soft) px-3.5 py-2.5 text-[13px] leading-[1.45] text-(--fg-1)',
  link: `inline-flex cursor-pointer items-center gap-1 rounded text-[13px] font-bold whitespace-nowrap text-(--app-accent-strong) hover:underline ${ui.focusRing}`,

  /** Label column of a key/value grid. */
  k: 'font-(family-name:--font-fallback) text-[11px] font-bold uppercase tracking-[0.08em] text-(--fg-3)',
  display: 'font-(family-name:--font-display) font-bold leading-[1.05] text-(--fg-1)',
  tag: 'inline-flex items-center rounded-full bg-(--app-accent-soft) px-2.5 py-1 text-xs font-bold text-(--app-accent-strong)',
  box: 'rounded-lg border border-(--border-subtle)',

  /** Drawer fields. */
  field: 'flex flex-col gap-2',
  fieldLabel: 'text-[13px] font-bold text-(--fg-1)',
  fieldSub: 'text-xs text-(--fg-3)',
  input: 'w-full rounded-lg border border-(--border-default) bg-(--mba-surface) px-3 py-2.5 font-(family-name:--font-fallback) text-sm text-(--fg-1) placeholder:text-(--fg-muted) focus:border-(--app-accent) focus:outline-none focus:ring-[3px] focus:ring-(--app-accent-ring) disabled:bg-(--bg-alt) disabled:text-(--fg-3)',
  highlight: 'ring-[3px] ring-[#e8a54b]',

  chipOff: `${chip} border-(--border-default) bg-transparent text-(--fg-2) hover:border-(--fg-3)`,
  chipOn: `${chip} border-(--app-accent) bg-(--app-accent-soft) text-(--app-accent-strong)`,

  optTile: `flex cursor-pointer items-center gap-3 rounded-lg border-[1.5px] border-(--border-default) bg-transparent px-3.5 py-3 text-left transition-colors duration-[140ms] hover:border-(--fg-3) disabled:cursor-not-allowed disabled:opacity-50 ${ui.focusRing}`,
  optTileOn: `flex cursor-pointer items-center gap-3 rounded-lg border-[1.5px] border-(--app-accent) bg-(--app-accent-soft) px-3.5 py-3 text-left disabled:cursor-not-allowed ${ui.focusRing}`,

  segTrack: 'inline-flex gap-0.5 rounded-lg bg-(--bg-alt) p-[3px]',
  segOff: `${segBtn} bg-transparent text-(--fg-2) hover:text-(--fg-1)`,
  segOn: `${segBtn} bg-(--mba-surface) text-(--fg-1) shadow-[0_1px_2px_rgba(0,0,0,0.08)]`,

  /** The small add / remove controls in list editors. */
  addLink: `inline-flex cursor-pointer items-center gap-1.5 self-start rounded-md py-1 text-[13px] font-bold text-(--app-accent-strong) hover:underline ${ui.focusRing}`,
} as const;
